/**
 * useSidebarBadges — fetch ligero para popular badges del sidebar.
 *
 * Polling cada 60s mientras el tab está activo.
 * Cuenta:
 *   - tareas.por_aprobar
 *   - tareas.requieren_atencion
 *   - pedidos.pendientes
 *   - reportes.nuevo (último mes sin abrir)
 */

import { useEffect, useState, useCallback } from "react";
import { api } from "./api.js";

export function useSidebarBadges(clienteActivoId) {
  const [badges, setBadges] = useState({
    aprobar: 0,
    tareas_atencion: 0,
    pedidos_pendientes: 0,
    reporte_nuevo: false,
  });

  const refresh = useCallback(async () => {
    if (!clienteActivoId) return;
    try {
      const [dash, pedidos, reportes] = await Promise.all([
        api.dashboard(clienteActivoId).catch(() => null),
        api.pedidos(clienteActivoId).catch(() => ({ pedidos: [] })),
        api.reportes(clienteActivoId).catch(() => ({ reportes: [] })),
      ]);

      const pendientesPedido = (pedidos.pedidos || []).filter(
        p => p.estado === "pendiente" || p.estado === "en-proceso"
      ).length;

      // Reporte "nuevo": último reporte publicado en últimos 7 días
      const ultReporte = (reportes.reportes || [])[0];
      const reporteNuevo = ultReporte?.generated_at
        ? (Date.now() - new Date(ultReporte.generated_at).getTime() < 7 * 24 * 60 * 60 * 1000)
        : false;

      setBadges({
        aprobar: dash?.kpis?.por_aprobar || 0,
        // kpis.por_aprobar es el total real; necesitan_atencion viene recortada a 10 (el badge decía 11 con 18 pendientes).
        tareas_atencion: dash?.kpis?.por_aprobar ?? (dash?.necesitan_atencion || []).length,
        pedidos_pendientes: pendientesPedido,
        reporte_nuevo: reporteNuevo,
      });
    } catch (err) {
      console.warn("[useSidebarBadges]", err.message);
    }
  }, [clienteActivoId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh cada 60s si el tab está visible
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 60_000);
    return () => clearInterval(interval);
  }, [refresh]);

  // Refresh al volver al tab
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  return { badges, refresh };
}
